/*
 * The dashboard: what is happening, right now, without scrolling.
 *
 * The order of this page is the order of the questions actually asked when
 * somebody opens it during market hours:
 *
 *   1. Is anything wrong?          — the banners, and nothing above them
 *   2. What has today made?        — the tiles
 *   3. What am I holding?          — open positions, marked live
 *   4. What are they doing?        — the watchlist, ticking
 *   5. Why did it do that?         — the decision log
 *
 * The banners come first and are not dismissible, because every one of them
 * is a reason the thing is not trading: no Zerodha session, halted, engine
 * off, a strategy that stopped on its loss limit. On a morning when nothing
 * has happened by ten o'clock, the answer is always one of those, and it
 * should not have to be looked for.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  usePerformance,
  usePositions,
  useSeedQuotes,
  useSignals,
  useStatus,
  useStrategies,
  useSummary,
} from "../api/hooks";
import { useLive, useWatch } from "../api/stream";
import { IconBolt, IconCheck, IconWarning } from "../components/Icons";
import { LiveChange, LivePriceCell } from "../components/LivePrice";
import {
  clockFromMs,
  compactRupees,
  pct,
  price as fmtPrice,
  rupees,
  signedRupees,
  toneOf,
} from "../lib/format";

export function DashboardPage() {
  const status = useStatus();
  const summary = useSummary(status.data?.marketOpen);
  const performance = usePerformance(status.data?.marketOpen);
  const strategies = useStrategies();
  const positions = usePositions(true, status.data?.marketOpen);
  const signals = useSignals(12, status.data?.marketOpen);

  const account = status.data;
  const live = performance.data?.live;
  const paper = performance.data?.paper;

  /* The environment decides, now that there is one. No guessing from what
     happens to be open — the switch in the header is the answer. */
  const onPaper = (account?.environment ?? "paper") === "paper";
  const showing: "paper" | "live" = onPaper ? "paper" : "live";
  const shown = onPaper ? paper : live;
  const deployed = (onPaper ? account?.paperDeployed : account?.deployed) ?? 0;
  const purse = (onPaper ? account?.paperCapitalRupees : account?.capitalRupees) ?? 0;

  /* Every instrument on the page, subscribed once. The positions and the
     watchlist overlap heavily, and the union is what the socket is told. */
  const watched = useMemo(() => {
    const tokens = new Set<number>();
    positions.data?.forEach((p) => tokens.add(p.instrumentToken));
    strategies.data?.forEach((s) => s.watches.forEach((w) => tokens.add(w.instrumentToken)));
    return [...tokens];
  }, [positions.data, strategies.data]);

  useWatch(watched);
  // Fills the tables before the socket's first frame, which on an illiquid
  // name can be minutes away.
  useSeedQuotes(watched, !!account?.sessionLive);

  return (
    <>
      <Banners />

      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p>
            {account?.marketOpen
              ? "The market is open. Strategies run on their own; nothing here needs you."
              : "The market is closed. What follows is where the day finished."}
          </p>
        </div>
      </div>

      {/* Paper and live are separate accounts now, so the tiles follow
          whichever one is actually running. A board showing four zeroes
          because everything is on paper is a board that looks broken. */}
      <div className="grid cols-4 mb">
        <div className="stat">
          <span className="k">Realised today</span>
          <span className={`v ${toneOf(summary.data?.realised)}`}>
            {signedRupees(summary.data?.realised ?? 0)}
          </span>
          <span className="note">
            {summary.data?.trades ?? 0} trade{summary.data?.trades === 1 ? "" : "s"} closed
          </span>
        </div>

        <div className="stat">
          <span className="k">
            Open P&amp;L
            <span className={`pill ${showing}`}>{showing}</span>
          </span>
          <span className={`v ${toneOf(shown?.unrealised)}`}>
            {signedRupees(shown?.unrealised ?? 0)}
          </span>
          <span className="note">
            {shown?.open ?? 0} position{shown?.open === 1 ? "" : "s"} ·{" "}
            {rupees(shown?.invested ?? 0)} in
          </span>
        </div>

        <div className="stat">
          <span className="k">
            Deployed
            <span className={`pill ${showing}`}>{showing}</span>
          </span>
          <span className="v">{compactRupees(deployed)}</span>
          <span className="note">
            of {compactRupees(purse)}
            {purse ? ` · ${Math.round((deployed / purse) * 100)}%` : ""}
          </span>
        </div>

        <div className="stat">
          <span className="k">{onPaper ? "Paper, all time" : "Live, all time"}</span>
          <span className={`v ${toneOf(shown?.total)}`}>{signedRupees(shown?.total ?? 0)}</span>
          <span className="note">
            {shown?.trades ?? 0} closed · {shown?.winRate ?? 0}% won
          </span>
        </div>
      </div>

      <div className="grid main-side">
        <div className="col">
          <OpenPositions />
          <Watchlist />
        </div>
        <div className="col">
          <StrategyStrip />
          <RecentSignals />
        </div>
      </div>

      {/* The one figure on this page that is not what it appears to be, said
          where it is shown rather than in a help page nobody opens. */}
      <p className="tiny faint mt">
        Every P&amp;L figure here is gross of brokerage, taxes and slippage.
        Zerodha's actual charges are known only on the contract note — Settings
        shows an estimate.
        {onPaper && purse > (account?.capitalRupees ?? 0) && (
          <>
            {" "}Paper runs on a simulated {compactRupees(purse)} against your real{" "}
            {compactRupees(account?.capitalRupees ?? 0)}, so these figures are the
            shape of the rule rather than what your account would have made — scale
            them down by about{" "}
            {Math.round(purse / Math.max(1, account?.capitalRupees ?? 1))}×.
          </>
        )}
        {signals.isError && " Signals could not be loaded."}
      </p>
    </>
  );
}

/* ─────────────────────────── banners ─────────────────────────── */

function Banners() {
  const status = useStatus();
  const strategies = useStrategies();
  const account = status.data;

  if (!account) return null;

  const stopped = (strategies.data ?? []).filter((s) => s.lastError);

  return (
    <>
      {!account.connected && (
        <div className="banner info">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>No Zerodha account connected</strong>
            <p>
              Add your Kite Connect API key and secret in Settings, then sign in to
              Zerodha. Until then nothing can be priced and nothing can be traded.
            </p>
          </div>
          <Link className="btn sm" to="/settings">Settings</Link>
        </div>
      )}

      {account.connected && !account.sessionLive && (
        <div className="banner warn">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>Today's Zerodha session has not been opened</strong>
            <p>
              Zerodha ends every API session overnight and SEBI does not allow a
              broker to reopen one without a person. This is the one thing that
              needs doing each morning — after it, the day runs on its own.
            </p>
          </div>
          {account.loginUrl && (
            <a className="btn primary sm" href={account.loginUrl}>
              Sign in to Zerodha
            </a>
          )}
        </div>
      )}

      {account.connected && account.halted && (
        <div className="banner danger">
          <span className="ico"><IconBolt size={17} /></span>
          <div className="body">
            <strong>Trading is halted</strong>
            <p>
              {account.haltReason
                ? account.haltReason
                : "Nothing new will be opened until this is cleared by hand."}
            </p>
          </div>
          <Link className="btn sm" to="/settings">Clear it</Link>
        </div>
      )}

      {account.sessionLive && !account.halted && !account.engineEnabled && (
        <div className="banner warn">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>The engine is switched off</strong>
            <p>
              Everything is connected, but no strategy will be run. This is a server
              setting, not something the screen can change.
            </p>
          </div>
        </div>
      )}

      {stopped.map((s) => (
        <div className="banner warn" key={s.id}>
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>{s.name} stopped</strong>
            <p>{s.lastError}</p>
          </div>
          <Link className="btn sm" to={`/strategies/${s.id}`}>Open</Link>
        </div>
      ))}
    </>
  );
}

/* ─────────────────────────── open positions ─────────────────────────── */

function OpenPositions() {
  const status = useStatus();
  const positions = usePositions(true, status.data?.marketOpen);
  const live = useLive();
  const rows = positions.data ?? [];

  /* Marked from the socket rather than from the server's own `unrealised`,
     which is as old as the last poll. The arithmetic is display-only — no
     figure computed here ever reaches an order. */
  const total = rows.reduce((sum, p) => {
    const last = live.quote(p.instrumentToken)?.last ?? p.lastPrice ?? p.entryPrice;
    return sum + (last - p.entryPrice) * p.quantity;
  }, 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Open positions</h2>
        <span className={`sub num ${toneOf(total)}`}>
          {rows.length > 0 ? signedRupees(total, 0) : ""}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="empty">
          <strong>Nothing open</strong>
          Positions appear here the moment a strategy opens one.
        </div>
      ) : (
        <div className="table-wrap capped" style={{ ["--rows" as string]: 6 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="num">Qty</th>
                <th className="num">Entry</th>
                <th className="num">Last</th>
                <th className="num">P&amp;L</th>
                <th className="num">Stop / Target</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const last = live.quote(p.instrumentToken)?.last ?? p.lastPrice ?? p.entryPrice;
                const pnl = (last - p.entryPrice) * p.quantity;
                const movePct = ((last - p.entryPrice) / p.entryPrice) * 100;
                return (
                  <tr key={p.id}>
                    <td>
                      <div className="sym">{p.tradingsymbol}</div>
                      <div className="sub">{p.exchange}</div>
                    </td>
                    <td className="num">{p.quantity}</td>
                    <td className="num">{fmtPrice(p.entryPrice)}</td>
                    <LivePriceCell token={p.instrumentToken} fallback={p.lastPrice} />
                    <td className={`num ${toneOf(pnl)}`}>
                      {signedRupees(pnl, 0)}
                      <div className="sub">{pct(movePct)}</div>
                    </td>
                    <td className="num small faint">
                      {p.stopPrice ? fmtPrice(p.stopPrice) : "—"} /{" "}
                      {p.targetPrice ? fmtPrice(p.targetPrice) : "—"}
                    </td>
                    <td>
                      <span className={`pill ${p.mode}`}>{p.mode}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── watchlist ─────────────────────────── */

function Watchlist() {
  const strategies = useStrategies();
  const live = useLive();

  /* One row per instrument, however many strategies watch it — the same stock
     under three rules is one price, not three. */
  const rows = useMemo(() => {
    const seen = new Map<number, { symbol: string; exchange: string; rules: string[] }>();
    for (const s of strategies.data ?? []) {
      for (const w of s.watches) {
        const existing = seen.get(w.instrumentToken);
        if (existing) existing.rules.push(s.name);
        else
          seen.set(w.instrumentToken, {
            symbol: w.tradingsymbol,
            exchange: w.exchange,
            rules: [s.name],
          });
      }
    }
    return [...seen.entries()].map(([token, v]) => ({ token, ...v }));
  }, [strategies.data]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Watchlist</h2>
        <span className="sub">{rows.length} instrument{rows.length === 1 ? "" : "s"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="empty">
          <strong>Nothing being watched</strong>
          Add instruments to a strategy and they appear here, live.
        </div>
      ) : (
        <div className="table-wrap capped" style={{ ["--rows" as string]: 6 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="num">Last</th>
                <th className="num">Change</th>
                <th className="num">Day range</th>
                <th>Watched by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const q = live.quote(r.token);
                return (
                  <tr key={r.token}>
                    <td>
                      <div className="sym">{r.symbol}</div>
                      <div className="sub">{r.exchange}</div>
                    </td>
                    <LivePriceCell token={r.token} />
                    <td className="num">
                      <LiveChange token={r.token} />
                    </td>
                    <td className="num small faint">
                      {q?.low ? `${fmtPrice(q.low)} – ${fmtPrice(q.high)}` : "—"}
                    </td>
                    <td className="small faint">{r.rules.join(", ")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── strategies ─────────────────────────── */

function StrategyStrip() {
  const strategies = useStrategies();
  const rows = strategies.data ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h2>Strategies</h2>
        <Link className="btn sm ghost" to="/strategies">All</Link>
      </div>
      {rows.length === 0 ? (
        <div className="empty">
          <strong>No strategies yet</strong>
          A strategy is a rule, a few stocks and a size.
        </div>
      ) : (
        <div className="card-body col capped-list" style={{ gap: 8, ["--rows" as string]: 5 }}>
          {rows.map((s) => (
            <Link
              key={s.id}
              to={`/strategies/${s.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "8px 10px",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              <span
                className="pulse"
                style={{
                  color: s.isEnabled ? "var(--up)" : "var(--text-faint)",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                <div className="tiny faint">
                  {s.watches.length} instrument{s.watches.length === 1 ? "" : "s"} ·{" "}
                  {s.interval}
                </div>
              </div>
              <span className={`pill ${s.mode}`}>{s.mode}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── signals ─────────────────────────── */

/**
 * The decision log.
 *
 * This answers the question this application is asked most: "why did nothing
 * happen?" A signal that was generated and then refused by a risk rule is
 * recorded with the reason, and that is far more useful than silence.
 */
function RecentSignals() {
  const status = useStatus();
  const signals = useSignals(12, status.data?.marketOpen);
  const rows = signals.data ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h2>Decisions</h2>
        <Link className="btn sm ghost" to="/signals">All</Link>
      </div>
      {rows.length === 0 ? (
        <div className="empty">
          <strong>No decisions yet</strong>
          Every signal a rule produces is logged here, acted on or not.
        </div>
      ) : (
        <div className="card-body col capped-list" style={{ gap: 9, ["--rows" as string]: 4, ["--row-h" as string]: "84px" }}>
          {rows.map((s) => (
            <div key={s.id} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <span className={`pill ${s.side === "BUY" ? "buy" : "sell"}`} style={{ marginTop: 2 }}>
                {s.side}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5 }}>
                  <strong>{s.tradingsymbol}</strong>{" "}
                  <span className="faint">at {fmtPrice(s.price)}</span>
                </div>
                <div className="tiny faint">{s.reason}</div>
                {!s.acted && s.skippedReason && (
                  <div className="tiny warn">Not taken — {s.skippedReason}</div>
                )}
                {s.acted && (
                  <div className="tiny up">
                    <IconCheck size={11} /> Order placed
                  </div>
                )}
              </div>
              <span className="tiny faint nowrap">{clockFromMs(s.atMs)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

